import React, { useEffect, useRef } from 'react';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/**
 * Cloudflare Turnstile widget.
 * IMPORTANT — tokens are ONE-SHOT and expire after ~5 min. The widget must
 * be mounted ONCE per page-life of the form, NOT re-rendered on every
 * parent re-render or prop change, otherwise the captured token is stale
 * and Cloudflare returns "invalid-input-response".
 *
 * We stabilize the effect by storing onToken/action in refs and depending
 * ONLY on theme/size (which rarely change).
 */
export default function Turnstile({ onToken, theme = 'light', size = 'flexible', action = 'auth' }) {
  const ref = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const actionRef = useRef(action);

  // Keep refs current without re-running the effect
  useEffect(() => { onTokenRef.current = onToken; }, [onToken]);
  useEffect(() => { actionRef.current = action; }, [action]);

  useEffect(() => {
    if (!SITE_KEY) {
      onTokenRef.current && onTokenRef.current('dev');
      return;
    }

    if (!document.getElementById(TURNSTILE_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    let mounted = true;
    const tryRender = () => {
      if (!mounted) return;
      if (window.turnstile && ref.current && widgetIdRef.current === null) {
        try {
          widgetIdRef.current = window.turnstile.render(ref.current, {
            sitekey: SITE_KEY,
            theme,
            size,
            action: actionRef.current,
            // Fresh token issued — push to parent
            callback: (token) => { onTokenRef.current && onTokenRef.current(token); },
            // Token expired (5 min) — clear parent state to force a new challenge before submit
            'expired-callback': () => {
              onTokenRef.current && onTokenRef.current(null);
              try { window.turnstile.reset(widgetIdRef.current); } catch (e) { /* ignore */ }
            },
            // Network/render error — clear and retry
            'error-callback': () => {
              onTokenRef.current && onTokenRef.current(null);
              try { window.turnstile.reset(widgetIdRef.current); } catch (e) { /* ignore */ }
            },
            'refresh-expired': 'auto',
          });
        } catch (e) {
          /* widget already rendered or container detached — ignore */
        }
      } else {
        setTimeout(tryRender, 300);
      }
    };
    tryRender();

    return () => {
      mounted = false;
      try {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (e) { /* ignore */ }
      widgetIdRef.current = null;
    };
    // Mount widget ONCE — never re-create on prop changes (onToken/action/mode swap).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, size]);

  // Expose a global reset hook so the form can force a fresh token after a failed submit
  useEffect(() => {
    window.__resetTurnstile = () => {
      try {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          onTokenRef.current && onTokenRef.current(null);
        }
      } catch (e) { /* ignore */ }
    };
    return () => { try { delete window.__resetTurnstile; } catch (e) { /* ignore */ } };
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={ref} data-testid="turnstile-widget" className="flex justify-center" />;
}
